import store from "@/store";
import { formatDate } from "@/helpers/common.js";

/**
 * Evaluates a single achievement against a user's earned codes.
 *
 * This function determines how much progress a user has made toward a specific
 * achievement and whether the achievement is fully earned. It analyzes the
 * user's codes to check:
 *   - Which of the achievement's required codes the user has
 *   - The latest timestamp among the earned codes
 *   - Whether the user's score meets the achievement's target score
 *
 * It returns a full achievement object containing:
 *   - isEarned: Whether the achievement is earned
 *   - codesEarned: How many of its required codes the user currently has
 *   - tsLastUpdated: Latest timestamp of the earned codes (or null)
 *   - formattedDate: Human-readable version of that timestamp
 *   - score: The user's score for the matching code (or 0 if none)
 *   - timestamps: All timestamps for the earned codes
 *
 * @param {Object} achievement - The achievement definition (name, targetScore, codes).
 * @param {Array} userCodes - List of code objects the user has earned, including timestamps and scores.
 * @returns {Object} A new achievement object annotated with progress and completion details.
 */
function evaluateAchievement(achievement, userCodes) {
  const codeMap = new Map(userCodes.map((c) => [c.code, c]));
  const earnedCodes = new Set(userCodes.map((c) => c.code));

  // Find which codes match this achievement
  const matchingCode = achievement.codes.find((code) => earnedCodes.has(code));
  const codeObject = matchingCode ? codeMap.get(matchingCode) : null;

  const isEarned = codeObject && codeObject.score >= achievement.targetScore;

  const timestamps = achievement.codes
    .filter((code) => earnedCodes.has(code))
    .map((code) => codeMap.get(code).tsLastUpdated);

  const latestTimestamp = timestamps.length ? Math.max(...timestamps) : null;

  const numberOfCodesEarned = achievement.codes.filter((code) =>
    earnedCodes.has(code)
  ).length;

  return {
    ...achievement,
    isEarned,
    codesEarned: numberOfCodesEarned,
    tsLastUpdated: latestTimestamp,
    formattedDate: latestTimestamp ? formatDate(latestTimestamp) : null,
    score: codeObject ? codeObject.score : 0,
    timestamps
  };
}

/**
 * Calculates the total points a user has earned based on their achievement codes.
 *
 * It filters achievements to only those fully earned by the user (all required codes present),
 * then sums up their points.
 *
 * @param {Array} userAchievements - Array of user achievement code objects.
 * @returns {number} Total achievement points earned by the user.
 */
function calculateAchievementPoints(userAchievements) {
  let earnedCodes = [];
  let achievementsData = store.getters["content/getAchievements"];

  // Filter userAchievements to only include those where the user's score meets or exceeds the targetScore
  userAchievements = userAchievements.filter((userCode) => {
    // Find the achievement that contains this code
    const achievement = achievementsData.find(
      (achievement) => achievement.codes.includes(userCode.code) // Check if the code is part of this achievement
    );
    if (achievement) {
      // Check if the user's score meets or exceeds the targetScore
      return userCode.score >= achievement.targetScore;
    }
    return false; // If no matching achievement found, exclude the code
  });

  // Get an array of just earned codes
  earnedCodes = userAchievements.map((achievement) => achievement.code);

  // Filter the achievements to only include those where all codes are earned
  const earnedAchievements = achievementsData.filter((achievement) =>
    achievement.codes.every((code) => earnedCodes.includes(code))
  );

  // Go through earnedAchievements and calculate all points as a sum, theres a points property
  const totalPoints = earnedAchievements.reduce(
    (sum, a) => sum + (a.points || 0),
    0
  );

  return totalPoints;
}

export { evaluateAchievement, calculateAchievementPoints };
